// src/pages/admin/ClientsPageList.ts

import { useEffect, useState, Fragment } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Alert,
  Form,
  Spinner,
} from "react-bootstrap";
import AddClientModal from "components/admin/AddClientsModal";
import EditClientModal from "components/admin/EditClientModal";
import ViewUsersModal from "components/admin/ViewUsersModal";
import ViewDevicesModal from "components/admin/ViewDevicesModal";
import http from "services/httpClient";

import { useAuth } from "context/AuthContext";

interface ClientData {
  id: number;
  name: string;
  industry: string;
  device_count: number;
  user_count: number;
  created_at: string;

  // ✅ must be returned by GET /clients (recommended: join from client_features)
  sms_enabled?: boolean;
}

export const ClientsPageList = () => {
  // assumes your RootLayout provides `user` in outlet context
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [clients, setClients] = useState<ClientData[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<string>("success");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientData | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeClientId, setActiveClientId] = useState<number | null>(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);

  // per-client busy state for sms switch
  const [smsBusy, setSmsBusy] = useState<Record<number, boolean>>({});

  const fetchClients = async () => {
    try {
      const { data } = await http.get(`/clients`);
      setClients(data);
    } catch (err) {
      setAlertMessage("Failed to fetch clients.");
      setAlertVariant("danger");
    }
  };

  const handleClientAdded = async (_newClient: ClientData) => {
    setAlertMessage("Client created successfully.");
    setAlertVariant("success");

    await fetchClients(); // re-pulls sorted list from backend
    setTimeout(() => setAlertMessage(null), 3000);
  };

  const handleDeleteClient = async (clientId: number) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      await http.delete(`/clients/${clientId}`);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      setAlertMessage("Client deleted.");
      setAlertVariant("success");
    } catch (err) {
      setAlertMessage("Failed to delete client.");
      setAlertVariant("danger");
    }

    setTimeout(() => setAlertMessage(null), 3000);
  };

  const handleClientUpdated = (updated: ClientData) => {
    setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setAlertMessage("Client updated successfully.");
    setAlertVariant("success");
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // ✅ Toggle SMS Enabled (owner-only UI; backend should still enforce owner-only)
  const handleToggleSmsEnabled = async (clientId: number, nextValue: boolean) => {
    // optimistic update
    const prevClients = clients;
    setClients((prev) =>
      prev.map((c) => (c.id === clientId ? { ...c, sms_enabled: nextValue } : c))
    );

    setSmsBusy((prev) => ({ ...prev, [clientId]: true }));

    try {
      // Adjust this endpoint if yours is different
      await http.patch(`/client_features/${clientId}`, { sms_enabled: nextValue });

      setAlertMessage(`SMS ${nextValue ? "enabled" : "disabled"} for client.`);
      setAlertVariant("success");
    } catch (err) {
      // rollback if failed
      setClients(prevClients);
      setAlertMessage("Failed to update SMS setting.");
      setAlertVariant("danger");
    } finally {
      setSmsBusy((prev) => ({ ...prev, [clientId]: false }));
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <Fragment>
      <div className="bg-danger pt-5 pb-21"></div>
      <Container fluid className="mt-n22 px-6">
        <Row className="mt-6">
          <Col md={12}>
            <Card className="rounded-3 p-3 shadow-sm">
              <Card.Header className="bg-white py-4 d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Clients</h4>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Client
                </Button>
              </Card.Header>

              <Table responsive className="text-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Client Name</th>
                    <th>Industry</th>
                    <th>Users</th>
                    <th>Devices</th>

                    {/* ✅ owner-only column */}
                    {isOwner && <th>SMS Enabled</th>}

                    <th>Date Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td>{client.name}</td>
                      <td>{client.industry}</td>
                      <td>{client.user_count}</td>
                      <td>{client.device_count}</td>

                      {/* ✅ owner-only switch */}
                      {isOwner && (
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="switch"
                              id={`sms-enabled-${client.id}`}
                              checked={!!client.sms_enabled}
                              disabled={!!smsBusy[client.id]}
                              onChange={(e) =>
                                handleToggleSmsEnabled(client.id, e.target.checked)
                              }
                              label=""
                            />
                            {smsBusy[client.id] && (
                              <Spinner animation="border" size="sm" />
                            )}
                          </div>
                        </td>
                      )}

                      <td>{new Date(client.created_at).toLocaleString()}</td>

                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setActiveClientId(client.id);
                            setShowUserModal(true);
                          }}
                          className="me-1"
                        >
                          View Users
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => {
                            setActiveClientId(client.id);
                            setShowDeviceModal(true);
                          }}
                          className="me-1"
                        >
                          View Devices
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setEditingClient(client);
                            setShowEditModal(true);
                          }}
                          className="me-1"
                        >
                          Edit
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteClient(client.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {alertMessage && (
                <Alert
                  variant={alertVariant}
                  onClose={() => setAlertMessage(null)}
                  dismissible
                  className="mt-3"
                >
                  {alertMessage}
                </Alert>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {activeClientId !== null && (
        <ViewUsersModal
          clientId={activeClientId}
          show={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setActiveClientId(null);
          }}
          onUserChange={fetchClients}
        />
      )}

      {activeClientId !== null && (
        <ViewDevicesModal
          clientId={activeClientId}
          show={showDeviceModal}
          onClose={() => {
            setShowDeviceModal(false);
            setActiveClientId(null);
          }}
        />
      )}

      {editingClient && (
        <EditClientModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          client={editingClient}
          onClientUpdated={handleClientUpdated}
        />
      )}

      <AddClientModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onClientAdded={handleClientAdded}
      />
    </Fragment>
  );
};

export default ClientsPageList;